import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { ApiResponse } from '../../@types/ApiResponse';
import type { RequestStateQuery } from '../../@types/RequestStateQuery';
import type { MarketCode } from '../../@types/Token';
import { postJSON } from '../../fetch';
import useAuth from '../../hooks/useAuth';
import useBranch from '../branch/useBranch';
import { PF_QUERY_KEYS } from '../pf/queryKeys';
import type { DetailedVehicle } from './useDetailedVehicle';
import type { OdometerReading, OdometerUnit } from './useVehicle';

type Statuses =
  | 'NoStatusFilter'
  | 'Unsupported'
  | 'Enriched'
  | 'Unpublished'
  | 'AwaitingReview'
  | 'Published'
  | 'Sold'
  | 'Imported'
  | 'Deleted'
  | 'DeletedFromDms'
  | 'PartlyEnriched';

interface Vehicle {
  registrationNumber?: string;
  //mileage: number;
  options?: string[];
  modelYear?: number;
  oemId?: string;
  vin?: string;
  odometerReading: OdometerReading | null;
}

interface VehiclePosition {
  city: string;
  postalCode: string;
  longitude: number;
  latitude: number;
  region?: string;
}

interface VehicleResellerPackagingOption {
  id: string;
  title: string;
}

interface VehicleMedia {
  fileUrls: string[];
}
interface VehicleAd {
  price: number;
  shortDescription: string;
  description: string;
  externalUrl: string;
  oldPrice: number;
  privateLeasingMonthlyPayment: number;
  businessLeasingMonthlyPayment: number;
  deductibleVat: boolean;
  isAuctionItem: boolean;
  isDemoVersion: boolean;
  availableFrom?: string;
  autoUpdatePrice: boolean;
  autoUpdateShortDescription: boolean;
  autoUpdateDescription: boolean;
  autoUpdateOptions: boolean;
  hasManufacturerPackaging: boolean;
  hasMrfPackaging: boolean;
  resellerPackagingOptions?: VehicleResellerPackagingOption[];
  media: VehicleMedia[];
  isAdditionalDescriptionSupported: boolean;
}

interface VehicleContactOptions {
  id?: string;
  avatarUrl?: string;
  title?: string;
  displayName?: string;
  link?: string;
  email?: string;
  telephone?: string;
}

export interface CreateVehiclePayload {
  branch: string;
  marketCode: MarketCode;
  status: Statuses;
  vehicle: Vehicle;
  position: VehiclePosition;
  ad: VehicleAd;
  contactOptions?: VehicleContactOptions;
  managedBy?: string;
  inventoryStatus?: string;
  station?: {
    ofType: string;
    latitude?: number;
    longitude?: number;
    displayName?: string;
    description?: string;
    imageUrls?: string[];
    createdBy?: string;
    stationedAt?: string;
  };
  purchaseInfo?: {
    ofType: string;
  };
}

type CreateVehiclePayloadPartial = Omit<
  Omit<Omit<CreateVehiclePayload, 'branch'>, 'marketCode'>,
  'position'
>;
export interface CreateProcess {
  processId: string;
  itemId: string;
  itemType: string;
  metadata: {
    registrationNumber: string;
    vin: string;
  };
}

interface NewVehicleOptions {
  detailedVehicle: DetailedVehicle;
  odometerReadingValue: number;
  odometerReadingUnit: OdometerUnit;
  purchaseInfoOfType: string;
  managedBy?: string;
  oemId?: string;
  vin?: string;
  registrationNumber?: string;
  price?: number;
  inventoryStatus?: string;
  availableFrom?: string;
}

export const newOemVehicle = ({
  detailedVehicle,
  odometerReadingValue,
  odometerReadingUnit,
  purchaseInfoOfType,
  managedBy,
  oemId,
  vin,
  registrationNumber,
  price,
  inventoryStatus,
  availableFrom,
}: NewVehicleOptions): CreateVehiclePayloadPartial => ({
  status: 'Unpublished',
  vehicle: {
    oemId,
    modelYear: detailedVehicle?.modelYear,
    registrationNumber,
    vin,
    options: [],
    odometerReading: {
      value: odometerReadingValue,
      unit: odometerReadingUnit,
    },
  },
  managedBy,
  purchaseInfo: {
    ofType: purchaseInfoOfType,
  },
  inventoryStatus,
  ad: {
    autoUpdateDescription: false,
    autoUpdateOptions: false,
    autoUpdatePrice: false,
    autoUpdateShortDescription: false,
    businessLeasingMonthlyPayment: 0,
    deductibleVat: false,
    description: '',
    externalUrl: '',
    hasManufacturerPackaging: false,
    hasMrfPackaging: false,
    isAuctionItem: false,
    isDemoVersion: false,
    media: [],
    oldPrice: 0,
    price: price ?? 0,
    privateLeasingMonthlyPayment: 0,
    shortDescription: '',
    resellerPackagingOptions: [],
    isAdditionalDescriptionSupported: true,
    availableFrom,
  },
});

export interface CreateVehicleOemPayload {
  vehicle: CreateVehiclePayloadPartial;
  processId?: string;
  oemId?: string;
  actionRootProcessId?: string;
}

interface CreateVehicleResponse {
  vehicle: { waykeId: string };
  processId?: string;
  actionRootProcessId?: string;
}

const useCreateVehicleOem = (): [
  (payload: CreateVehicleOemPayload) => void,
  RequestStateQuery,
  CreateVehicleResponse | undefined,
] => {
  const { getToken } = useAuth();
  const [branch] = useBranch();
  const queryClient = useQueryClient();

  const mutation = useMutation<CreateVehicleResponse, undefined, CreateVehicleOemPayload>({
    mutationFn: async (payload: CreateVehicleOemPayload) => {
      const p: CreateVehiclePayload = {
        ...payload.vehicle,
        marketCode: branch?.marketCode!,
        branch: branch?.id!,
        position: {
          city: branch?.city!,
          latitude: branch?.latitude!,
          longitude: branch?.longitude!,
          postalCode: branch?.zip!,
        },
      };
      const vehicleResponse = await postJSON<ApiResponse<{ waykeId: string }>>(
        '/api/vehicle/oem',
        getToken()!,
        p,
      );

      // const processResponse = payload.processId
      //   ? await postJSON<ProcessResponse>(
      //       `${process.env.NEXT_PUBLIC_PROCESS_FLOW_URI}/commands/${branch?.id}/activities/initialize`,
      //       getToken()!,
      //       {
      //         processId: payload.processId,
      //         itemId: vehicleResponse.response.waykeId,
      //         itemType: 'urn:pf:item:type-of:passenger-car',
      //         metadata: {
      //           registrationNumber: payload.registrationNumber,
      //           vin: payload.vin,
      //         },
      //       } as CreateProcess,
      //     )
      //   : undefined;

      // const actionRootProcessResponse = payload.actionRootProcessId
      //   ? await postJSON<ProcessResponse>(
      //       `${process.env.NEXT_PUBLIC_PROCESS_FLOW_URI}/commands/${branch?.id}/activities/initialize`,
      //       getToken()!,
      //       {
      //         processId: payload.actionRootProcessId,
      //         itemId: vehicleResponse.response.waykeId,
      //         itemType: 'urn:pf:item:type-of:passenger-car',
      //         metadata: {
      //           registrationNumber: payload.registrationNumber,
      //           vin: payload.vin,
      //         },
      //       } as CreateProcess,
      //     )
      //   : undefined;

      return {
        vehicle: vehicleResponse.response,
        // processId: processResponse?.response?.id,
        // actionRootProcessId: actionRootProcessResponse?.response?.id,
      };
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [PF_QUERY_KEYS.activities] });
      await queryClient.invalidateQueries({ queryKey: [PF_QUERY_KEYS.activitiesByItemId] });
      await queryClient.invalidateQueries({ queryKey: [PF_QUERY_KEYS.activity] });
    },
  });

  const { mutate, isPending, isSuccess, isError, data } = mutation;

  return [
    (payload: CreateVehicleOemPayload) => mutate(payload),
    { isLoading: isPending, isSuccess, isError },
    data,
  ];
};

export default useCreateVehicleOem;
